import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Box } from '@mui/material'
import { MarkdownPreview } from '../../markdown/Markdown/MarkdownPreview'
import { decodeHtmlCharCode } from '../../../../../utils/html'
import { MarkdownType } from './types'
import { ResizableArea } from '../../../draggers/ResizableArea'
import Toggle from '../../toggle/Toggle'
import { ScrollSync, ScrollSyncNode } from 'scroll-sync-react'
import { muiTheme } from '../../../../../styles/muiTheme'
import { BorderColor } from '../../../../../styles/commonStyles'

type MarkdownSideBySideViewProps = {
  previewText: string
  markdownType: MarkdownType
  children: ReactNode
}

export const MarkdownSideBySideView = ({
  previewText,
  markdownType,
  children,
}: MarkdownSideBySideViewProps) => {
  const [resizeWidth, setResizeWidth] = useState(0)
  const [currentWidth, setCurrentWidth] = useState(0)
  const [currentHeight, setCurrentHeight] = useState(0)
  const [sbsOpen, setSbsOpen] = useState(true)
  const resizeRef = useRef<HTMLDivElement>(null)

  const toggleSbs = useCallback(() => {
    const nowOpenState = !sbsOpen
    const baseWidth = resizeRef.current?.offsetWidth || resizeWidth
    let newWidth = baseWidth - 15
    if (nowOpenState) {
      newWidth = baseWidth / 2
    }
    setSbsOpen(nowOpenState)
    setCurrentWidth(newWidth)
    setResizeWidth(baseWidth)
  }, [sbsOpen, resizeWidth])

  useEffect(() => {
    if (resizeRef.current && resizeWidth !== resizeRef.current.offsetWidth) {
      setResizeWidth(resizeRef.current.offsetWidth)
      setCurrentWidth(resizeRef.current.offsetWidth / 2)
      setCurrentHeight(resizeRef.current.clientHeight)
    }
  }, [resizeRef.current?.clientHeight, resizeWidth])

  return (
    <ScrollSync>
      <Box
        ref={resizeRef}
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          flexGrow: 1,
        }}
      >
        <ResizableArea
          width={currentWidth}
          height={currentHeight}
          maxWidth={resizeWidth}
        >
          <>
            <ScrollSyncNode group={'markdown'}>
              <Box
                sx={{
                  // 右側プレビューと縦スクロールを同期させるため、
                  // ScrollSyncNode配下を単一のスクロール可能なBoxにする。
                  overflow: 'scroll',
                  width: '100%',
                  height: '100%',
                  flex: '1 1 0%',
                }}
              >
                {children}
              </Box>
            </ScrollSyncNode>
            <Box
              sx={{
                zIndex: 1000,
                position: 'absolute',
                top: '4px',
                right: 0,
                flexShrink: 0,
                transition: muiTheme.transitions.create(
                  ['right'],
                  sbsOpen
                    ? {
                        easing: muiTheme.transitions.easing.easeOut,
                        duration: muiTheme.transitions.duration.enteringScreen,
                      }
                    : {
                        easing: muiTheme.transitions.easing.sharp,
                        duration: muiTheme.transitions.duration.leavingScreen,
                      }
                ),
              }}
            >
              <Toggle
                toggle={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleSbs()
                }}
                isOpen={sbsOpen}
                attach={'right'}
              />
            </Box>
          </>
        </ResizableArea>
        <ScrollSyncNode group={'markdown'}>
          <Box
            className={'markdown-body'}
            sx={{
              padding: 0,
              overflow: 'scroll',
              width: '100%',
              height: '100%',
              flex: '1 1 0%',
              borderLeft: `1px solid ${BorderColor.LIGHT_BLACK}`,
            }}
          >
            <MarkdownPreview
              text={decodeHtmlCharCode(previewText)}
              markdownType={markdownType}
            />
          </Box>
        </ScrollSyncNode>
      </Box>
    </ScrollSync>
  )
}
